import { Badge } from '#src/base/badge/badge.tsx';
import { Command } from '#src/base/command/command.tsx';
import { Popover } from '#src/base/popover/popover.tsx';
import { cn } from '#src/styling/cn.ts';
import { CaretDownIcon, CheckIcon, WarningIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

export interface SelectOption {
  label: string;
  value: string;
}

export interface MultiselectProps {
  options: readonly SelectOption[];
  values: readonly string[];
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  error?: string;
  onChange: (values: string[]) => void;
}

export const Multiselect = ({
  options,
  values,
  placeholder = 'Select',
  searchable = false,
  clearable = false,
  error,
  onChange,
}: MultiselectProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedValues = useMemo(() => {
    return options.filter(option => values.includes(option.value));
  }, [options, values]);

  const clearSelected = () => {
    onChange([]);
  };

  return (
    <div className="flex flex-col gap-1">
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        {/* Trigger */}
        <Popover.Trigger
          className={cn(
            'flex h-10 w-full cursor-pointer items-center justify-between rounded-sm border px-4 text-sm font-normal whitespace-nowrap transition-all outline-none',
            error
              ? 'border-destructive hover:border-destructive'
              : 'border-gray-300 hover:border-gray-400',
          )}
        >
          {/* Placholder */}
          {values.length === 0 && <div className="text-gray-400">{placeholder}</div>}
          {/* Selected Values */}
          {values.length <= 3 && (
            <div className="flex max-w-full grow space-x-1 overflow-hidden">
              {selectedValues.map(option => (
                <Badge key={option.value}>{option.label}</Badge>
              ))}
            </div>
          )}
          {/* Selected Count */}
          {values.length > 3 && <Badge>{values.length} selected</Badge>}
          {/* Chevron */}
          <CaretDownIcon
            className={cn(
              'ml-2 size-4 shrink-0 text-gray-400 transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </Popover.Trigger>
        {/* Content */}
        <Popover.Content className="w-(--radix-popover-trigger-width) p-0" align="start">
          <Command.Root>
            {/* Search Box */}
            {searchable && <Command.Input placeholder="Search options..." />}
            {/* List Container */}
            <Command.List>
              {/* No Results */}
              <Command.Empty>
                <span className="text-muted-foreground">No results found.</span>
              </Command.Empty>
              {/* Options List */}
              <Command.Group>
                {options.map(option => {
                  const isSelected = values.includes(option.value);
                  return (
                    <Command.Item
                      key={option.value}
                      onSelect={() => {
                        if (isSelected) {
                          const valuesWithoutOption = values.filter(
                            value => value !== option.value,
                          );
                          onChange(valuesWithoutOption);
                        } else {
                          onChange([...values, option.value]);
                        }
                      }}
                    >
                      {/* Checkbox */}
                      <div
                        className={cn(
                          'border-primary flex size-4 items-center justify-center rounded-sm border',
                          isSelected ? 'bg-primary' : 'opacity-50 [&_svg]:invisible',
                        )}
                      >
                        <CheckIcon className="text-primary-foreground size-3" />
                      </div>
                      {/* Label */}
                      <span>{option.label}</span>
                    </Command.Item>
                  );
                })}
              </Command.Group>
              {/* Clear Selected */}
              {values.length > 0 && clearable && (
                <>
                  <Command.Separator />
                  <Command.Group>
                    <Command.Item
                      onSelect={clearSelected}
                      className="text-muted-foreground justify-center text-center"
                    >
                      Clear selected
                    </Command.Item>
                  </Command.Group>
                </>
              )}
            </Command.List>
          </Command.Root>
        </Popover.Content>
      </Popover.Root>
      {/* Error Message */}
      {error && (
        <div className="text-destructive flex items-center gap-1 text-xs">
          <WarningIcon className="size-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
